import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { RootState, useAppSelector } from '../../../../../state/state';
import { updateLiveSubscription } from '../../../../../state/LiveSubscriptionSlice/LiveSubscriptionSlice';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useBackend } from '../../../../../hooks/backend/useBackend';
import { selectEffectiveUID } from '../../../../../state/UserStatesV2/userSliceV2';

interface ProgressLog {
    id: string;
    timestamp: string;
    notes: string;
    imageLink?: string;
    videoLink?: string;
    type?: 'progress' | 'report';
}

interface Props {
    liveSubId: string;
    studentName: string;
}

export default function ProgressLogSection({ liveSubId, studentName }: Props) {
    const dispatch = useDispatch();
    const [isAdding, setIsAdding] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [newLog, setNewLog] = useState({
        notes: '',
        imageFile: null as File | null,
        videoFile: null as File | null,
        type: 'progress' as 'progress' | 'report',
    });

    const ServiceProviderId = useAppSelector(selectEffectiveUID);
    const { update } = useBackend<any>(`ServiceProviders/${ServiceProviderId}/LiveSubscriptions`);

    const liveSubscription = useAppSelector((state: RootState) => 
        state.LiveSubscriptionSlice.data?.find(sub => sub.id === liveSubId)
    );

    const progressLogs = [...(liveSubscription?.progress || [])]
        .map(log => ({
            ...log,
            type: (log as any).type || 'progress' // Use type assertion and default to 'progress'
        }))
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    const handleFileUpload = async (file: File, type: 'image' | 'video') => {
        if (!file) return null;
        
        const storage = getStorage();
        const fileExtension = file.name.split('.').pop();
        const fileName = `progress-logs/${liveSubId}/${Date.now()}-${type}.${fileExtension}`;
        const storageRef = ref(storage, fileName);

        try {
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            return downloadURL;
        } catch (error) {
            console.error('Error uploading file:', error);
            return null;
        }
    };

    const handleAddLog = async () => {
        if (!newLog.notes) return;
        setIsUploading(true);

        try {
            const [imageUrl, videoUrl] = await Promise.all([
                newLog.imageFile ? handleFileUpload(newLog.imageFile, 'image') : null,
                newLog.videoFile ? handleFileUpload(newLog.videoFile, 'video') : null
            ]);

            const newProgressLog: ProgressLog = {
                id: Date.now().toString(),
                timestamp: new Date().toISOString(),
                notes: newLog.notes,
                imageLink: imageUrl || null,
                videoLink: videoUrl || null,
                type: newLog.type,
            };

            const updatedProgress = [...progressLogs, newProgressLog].map(log => ({
                id: log.id,
                timestamp: log.timestamp,
                notes: log.notes,
                imageLink: log.imageLink || null,
                videoLink: log.videoLink || null,
                type: log.type || 'progress',
            }));
            
            dispatch(updateLiveSubscription({
                id: liveSubId,
                progress: updatedProgress
            }));
            
            await update(liveSubId, { progress: updatedProgress });

            setNewLog({ 
                notes: '', 
                imageFile: null, 
                videoFile: null, 
                type: 'progress'
            });
            setIsAdding(false);
        } catch (error) {
            console.error('Error adding log:', error);
        } finally {
            setIsUploading(false);
        }
    };

    const handleDeleteFile = async (fileUrl: string) => {
        if (!fileUrl) return;
        
        try {
            const storage = getStorage();
            const fileRef = ref(storage, fileUrl);
            await deleteObject(fileRef);
        } catch (error) {
            console.error('Error deleting file:', error);
        }
    };

    const handleDeleteLog = async (log: ProgressLog) => {
        try {
            if (log.imageLink) {
                await handleDeleteFile(log.imageLink);
            }
            if (log.videoLink) {
                await handleDeleteFile(log.videoLink);
            }

            const updatedProgress = progressLogs
                .filter(l => l.id !== log.id)
                .map(log => ({
                    id: log.id,
                    timestamp: log.timestamp,
                    notes: log.notes,
                    imageLink: log.imageLink || null,
                    videoLink: log.videoLink || null,
                    type: log.type || 'progress',
                }));
            
            dispatch(updateLiveSubscription({
                id: liveSubId,
                progress: updatedProgress
            }));
            
            await update(liveSubId, { progress: updatedProgress });
        } catch (error) {
            console.error('Error deleting log:', error);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">{studentName}'s Progress</h3>
                <button
                    onClick={() => setIsAdding(true)}
                    className="bg-cc_brand text-white px-3 py-1 rounded-md hover:bg-opacity-90"
                >
                    Add Log
                </button>
            </div>

            {isAdding && (
                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                    <div className="flex space-x-4 mb-2">
                        <label className="inline-flex items-center">
                            <input
                                type="radio"
                                checked={newLog.type === 'progress'}
                                onChange={() => setNewLog({...newLog, type: 'progress'})}
                                className="form-radio h-4 w-4 text-cc_brand"
                            />
                            <span className="ml-2">Progress Update</span>
                        </label>
                        <label className="inline-flex items-center">
                            <input
                                type="radio"
                                checked={newLog.type === 'report'}
                                onChange={() => setNewLog({...newLog, type: 'report'})}
                                className="form-radio h-4 w-4 text-cc_brand"
                            />
                            <span className="ml-2">Report</span>
                        </label>
                    </div>

                    <textarea
                        value={newLog.notes}
                        onChange={(e) => setNewLog({...newLog, notes: e.target.value})}
                        placeholder="Enter progress notes..."
                        className="w-full p-2 border rounded-md"
                        rows={3}
                    />
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => document.getElementById('file-upload')?.click()}
                                className="px-4 py-2 border rounded-md hover:bg-gray-100 flex items-center gap-2"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                    <polyline points="17 8 12 3 7 8"/>
                                    <line x1="12" y1="3" x2="12" y2="15"/>
                                </svg>
                                Upload Media
                            </button>
                            {(newLog.imageFile || newLog.videoFile) && (
                                <span className="text-sm text-gray-600">
                                    {newLog.imageFile?.name || newLog.videoFile?.name}
                                </span>
                            )}
                        </div>
                        <input
                            id="file-upload"
                            type="file"
                            accept="image/*,video/*"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                    const isImage = file.type.startsWith('image/');
                                    setNewLog({
                                        ...newLog,
                                        imageFile: isImage ? file : null,
                                        videoFile: !isImage ? file : null
                                    });
                                }
                            }}
                            className="hidden"
                        />
                    </div>
                    <div className="flex justify-end gap-2">
                        <button
                            onClick={() => setIsAdding(false)}
                            className="px-3 py-1 border rounded-md hover:bg-gray-100"
                            disabled={isUploading}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleAddLog}
                            className="bg-cc_brand text-white px-3 py-1 rounded-md hover:bg-opacity-90 disabled:opacity-50"
                            disabled={isUploading}
                        >
                            {isUploading ? 'Uploading...' : 'Save'}
                        </button>
                    </div>
                </div>
            )}

            <div className="space-y-3">
                {progressLogs.length === 0 ? (
                    <div className="bg-gray-50 p-4 rounded-lg border text-center text-gray-500">
                        No progress logs available
                    </div>
                ) : (
                    progressLogs.map((log) => (
                        <div 
                            key={log.id} 
                            className={`p-4 rounded-lg border ${
                                log.type === 'report' 
                                    ? 'bg-amber-50 border-amber-200' 
                                    : 'bg-white'
                            }`}
                        >
                            <div className="flex justify-between items-start">
                                <div>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        log.type === 'report'
                                            ? 'bg-amber-100 text-amber-800'
                                            : 'bg-blue-100 text-blue-800'
                                    }`}>
                                        {log.type === 'report' ? 'Report' : 'Progress Update'}
                                    </span>
                                    <span className="text-sm text-gray-500 ml-2">
                                        {new Date(log.timestamp).toLocaleString()}
                                    </span>
                                </div>
                                <button
                                    onClick={() => handleDeleteLog(log)}
                                    className="text-red-500 hover:text-red-700"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M3 6h18"></path>
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                    </svg>
                                </button>
                            </div>
                            <p className="mt-2">{log.notes}</p>
                            {(log.imageLink || log.videoLink) && (
                                <div className="mt-3">
                                    {log.imageLink && (
                                        <div className="mt-2">
                                            <img src={log.imageLink} alt="Progress" className="max-h-64 rounded-lg" />
                                        </div>
                                    )}
                                    {log.videoLink && (
                                        <div className="mt-2">
                                            <video controls className="max-h-64 rounded-lg">
                                                <source src={log.videoLink} type="video/mp4" />
                                                Your browser does not support the video tag.
                                            </video>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
} 